/**
 *
 * App.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 * NOTE: while this component should technically be a stateless functional
 * component (SFC), hot reloading does not currently support SFCs. If hot
 * reloading is not a necessity for you then you can refactor it and remove
 * the linting exception.
 */

import React, { Component } from 'react';
import { Switch, BrowserRouter as Router } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import _ from 'lodash';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ApolloProvider } from 'react-apollo';
import { withTranslation } from 'react-i18next';
import injectReducer from '@utils/injectReducer';
import injectSaga from '@utils/injectSaga';
import { ROUTES } from '@shared/constants';
import { graphClient } from '@services/commService';
import VideoChat from '@shared/VideoChatV2';
import MeetingEnded from '@shared/VideoChatV2/components/MeetingEnded';
import PopUp from '@containers/App/containers/PopUp';
import { STRIPE_TOKEN } from '@services/servers';
import SectionLoading from '@components/SectionLoading';
import UsersTable from '@containers/Admin/containers/UsersTable';
import { USER_PAGE_TYPES } from '@containers/Admin/containers/UserPage/consts';
import { UserPageWrapper } from '@containers/Admin/containers/UserPage/user-page-wrapper';
import { theme as muiTheme } from '@services/theme';
import PreAuthLocaleToggle from '@containers/LocaleToggle/PreAuthLocaleToggle';
import GlobalStyles from './globalStyles'; // import global styles
import HomePage from '../HomePage/Loadable';
import { Wrapper, MainPane } from './styles';
import ProtectedRoute from './components/ProtectedRoute';
import AuthenticationRoute from './components/AuthenticationRoute';
import UnprotectedRoute from './components/UnprotectedRoute';
import NotFoundRoute from './components/NotFoundRoute';
// Admin Containers
import PendingDashboard from '../Admin/containers/PendingDashboard';
import SupervisorDashboard from '../Admin/containers/SupervisorDashboard';
import ActivityPage from '../Admin/containers/ActivityPage';
import MediaPage from '../Admin/containers/MediaPage';
import Builder from '../Admin/containers/Builder';
import RoleManagement from '../Admin/containers/RoleManagement';
import ResourceManager from '../Admin/containers/ResourceManager';
import BusinessUnits from '../Admin/containers/BusinessUnits';
import Invites from '../Admin/containers/Invites';
import Analytics from '../Admin/containers/Analytics';

// App Containers
import SignIn from './containers/Authentication/SignIn';
import MFAVerification from './containers/Authentication/MFAVerification';
import SignUp from './containers/Authentication/SignUp';
import CompleteSignUp from './containers/Authentication/CompleteSignUp';
import ForgotPassword from './containers/Authentication/ForgotPassword';
import ResetPassword from './containers/Authentication/ResetPassword';
import { VerifyEmailSent } from './containers/Authentication/VerifyEmailSent';
import ValidatingView from './components/ValidatingView';
import CourseRouter from '../User/containers/CourseRouter';
import Billing from '../User/containers/Billing';
import Messaging from './containers/Messaging';
import Settings from './containers/Settings';

// Global components
import NavigationBar from './containers/NavigationBar';
import Redirector from './components/Redirector';
import PollingService from './components/PollingService';
import ZohoDeskAsapManager from './components/ZohoDeskAsapManager';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';

import saga from './saga';
import reducer from './reducer';
import {
  setTheme,
  getUserRoles,
  getServerProperties,
} from './actions';
import {
  getFromLocalStorage,
  LOCAL_STORAGE_ITEM,
} from '../../utils/localStorageHelper';
import { StaffList } from '@containers/Admin/containers/StaffList';
import { StaffPageWrapper } from '@containers/Admin/containers/StaffPage/staff-page-wrapper';

const stripePromise = loadStripe(STRIPE_TOKEN);

export class App extends Component {
  constructor(props) {
    super(props);
    props.setTheme('novotalk');
    this.state = {
      userRolesLocal: null,
    };
  }

  componentDidMount() {
    this.userRolesHandler();
    this.props.getServerProperties();
  }

  componentDidUpdate() {
    this.userRolesHandler();
  }

  userRolesHandler() {
    if (!this.state.userRolesLocal) {
      const rolesEnum = getFromLocalStorage(LOCAL_STORAGE_ITEM.USER_ROLES);

      if (rolesEnum) {
        this.setState({ userRolesLocal: rolesEnum });
      } else {
        this.props.getUserRoles();
      }
    }
  }

  render() {
    let theme = this.props.theme === 'novotalk' ? muiTheme : {};
    const { user, token } = this.props;
    theme = { ...theme, RTL: this.props.i18n.language === 'he' };

    const isAuthenticated = !_.isEmpty(user) && token;
    const { userRolesLocal } = this.state;
    return this.props.validating ? (
      <ValidatingView />
    ) : (
      <ApolloProvider client={graphClient}>
        <Router>
          <Elements stripe={stripePromise}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <Wrapper withNavBar={isAuthenticated}>
                  <GlobalStyles theme={theme} />
                  <PopUp />
                  <Redirector />
                  <PollingService />
                  <ZohoDeskAsapManager enabled={!!isAuthenticated} />
                  {isAuthenticated ? <NavigationBar user={user} /> : <PreAuthLocaleToggle />}
                  <MainPane withNavBar={isAuthenticated}>
                    <Switch>
                      <AuthenticationRoute
                        exact
                        path={ROUTES.SIGN_IN}
                        component={SignIn}
                      />
                    <AuthenticationRoute
                      exact
                      path={ROUTES.MFA_VERIFICATION}
                      component={MFAVerification}
                    />
                      <AuthenticationRoute
                        exact
                        path={ROUTES.SIGN_UP}
                        component={SignUp}
                      />
                      <AuthenticationRoute
                        exact
                        path={ROUTES.COMPLETE_SIGN_UP}
                        component={CompleteSignUp}
                      />
                      <UnprotectedRoute
                        exact
                        path={ROUTES.FORGOT_PASSWORD}
                        component={ForgotPassword}
                      />
                      <AuthenticationRoute
                        exact
                        path={ROUTES.RESET_PASSWORD}
                        component={ResetPassword}
                      />
                      <AuthenticationRoute
                        exact
                        path={ROUTES.VERIFY_EMAIL_SENT}
                        component={VerifyEmailSent}
                      />
                      <UnprotectedRoute
                        exact
                        path={ROUTES.TERMS_OF_USE}
                        component={TermsOfUse}
                      />
                      <UnprotectedRoute
                        exact
                        path={ROUTES.PRIVACY_POLICY}
                        component={PrivacyPolicy}
                      />
                      <UnprotectedRoute
                        exact
                        path={ROUTES.VIDEO_CHAT_ENDED}
                        component={MeetingEnded}
                      />

                      <ProtectedRoute
                        exact
                        path={ROUTES.HOME}
                        component={HomePage}
                        roles={[]}
                      />

                      {this.state.userRolesLocal ? (
                        <div>
                          <ProtectedRoute
                            exact
                            path={ROUTES.PENDING_DASHBOARD}
                            component={PendingDashboard}
                            roles={[
                              userRolesLocal.DEFAULT_MENTOR,
                              userRolesLocal.CLINICAL_MENTOR_HEB,
                              userRolesLocal.EMOTIONAL_MENTOR_HEB,
                              userRolesLocal.CLINICAL_MENTOR_EN,
                              userRolesLocal.EMOTIONAL_MENTOR_EN,
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                              userRolesLocal.MANAGEMENT_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.MEDIA_PAGE}
                            component={MediaPage}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.SUPERVISOR_DASHBOARD}
                            component={SupervisorDashboard}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                              userRolesLocal.MANAGEMENT_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path="/course/:courseIndex?/:skillIndex?/:skillPartIndex?/:exerciseIndex?"
                            component={CourseRouter}
                            roles={[]}
                            noRolesOnly
                          />
                          <ProtectedRoute
                            exact
                            path={`${ROUTES.ACTIVITY_PAGE}/:activityType/:activityId`}
                            component={ActivityPage}
                            roles={[]}
                          />
                          <ProtectedRoute
                            exact
                            path={`${ROUTES.VIDEO_CHAT}/:chatId?`}
                            component={VideoChat}
                            roles={[]}
                          />
                          <ProtectedRoute
                            exact
                            path={`${ROUTES.BUILDER}/:type/:id`}
                            component={Builder}
                            roles={[
                              userRolesLocal.TECH_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.ROLES_MANAGEMENT}
                            component={RoleManagement}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                              userRolesLocal.MANAGEMENT_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.BUSINESS_UNITS}
                            component={BusinessUnits}
                            roles={[
                              userRolesLocal.TECH_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.INVITES}
                            component={Invites}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                              userRolesLocal.MANAGEMENT_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.ANALYTICS}
                            component={Analytics}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={`${ROUTES.RESOURCES_MANAGER}/:type?/:id?`}
                            component={ResourceManager}
                            roles={[
                              userRolesLocal.TECH_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.BILLING}
                            component={Billing}
                            roles={[]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.MESSAGING}
                            component={Messaging}
                            roles={[]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.SETTINGS}
                            component={Settings}
                            roles={[]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.USERS.LIST}
                            component={UsersTable}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                              userRolesLocal.MANAGEMENT_ADMIN,
                              userRolesLocal.CLINICAL_MENTOR_EN,
                              userRolesLocal.CLINICAL_MENTOR_HEB,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={`${ROUTES.USERS.LIST}/:id/${USER_PAGE_TYPES.INFORMATION}`}
                            component={UserPageWrapper}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                              userRolesLocal.MANAGEMENT_ADMIN,
                              userRolesLocal.CLINICAL_MENTOR_EN,
                              userRolesLocal.CLINICAL_MENTOR_HEB,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={`${ROUTES.USERS.LIST}/:id/${USER_PAGE_TYPES.PERSONAL_PROGRAM}`}
                            component={UserPageWrapper}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                              userRolesLocal.MANAGEMENT_ADMIN,
                              userRolesLocal.CLINICAL_MENTOR_EN,
                              userRolesLocal.CLINICAL_MENTOR_HEB,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={`${ROUTES.USERS.LIST}/:id/${USER_PAGE_TYPES.LOG}`}
                            component={UserPageWrapper}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                              userRolesLocal.MANAGEMENT_ADMIN,
                              userRolesLocal.CLINICAL_MENTOR_EN,
                              userRolesLocal.CLINICAL_MENTOR_HEB,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={`${ROUTES.USERS.LIST}/:id/${USER_PAGE_TYPES.TIME_TRACKING}`}
                            component={UserPageWrapper}
                            roles={[
                              userRolesLocal.DEFAULT_MENTOR,
                              userRolesLocal.CLINICAL_MENTOR_EN,
                              userRolesLocal.CLINICAL_MENTOR_HEB,
                              userRolesLocal.EMOTIONAL_MENTOR_EN,
                              userRolesLocal.EMOTIONAL_MENTOR_HEB,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={`${ROUTES.USERS.LIST}/:id/${USER_PAGE_TYPES.MENTOR_FORMS}/:formActivityId?`}
                            component={UserPageWrapper}
                            roles={[
                              userRolesLocal.DEFAULT_MENTOR,
                              userRolesLocal.CLINICAL_MENTOR_EN,
                              userRolesLocal.CLINICAL_MENTOR_HEB,
                              userRolesLocal.EMOTIONAL_MENTOR_EN,
                              userRolesLocal.EMOTIONAL_MENTOR_HEB,
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={ROUTES.STAFF.LIST}
                            component={StaffList}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                              userRolesLocal.MANAGEMENT_ADMIN,
                            ]}
                          />
                          <ProtectedRoute
                            exact
                            path={`${ROUTES.STAFF.LIST}/:id/${USER_PAGE_TYPES.INFORMATION}`}
                            component={StaffPageWrapper}
                            roles={[
                              userRolesLocal.SUPERVISOR,
                              userRolesLocal.TECH_ADMIN,
                              userRolesLocal.MANAGEMENT_ADMIN,
                            ]}
                          />
                        </div>
                      ) : (
                        <SectionLoading />
                      )}

                      <NotFoundRoute />
                    </Switch>
                  </MainPane>
                  <ToastContainer />
                </Wrapper>
              </ThemeProvider>
            </StyledEngineProvider>
          </Elements>
        </Router>
      </ApolloProvider>
    );
  }
}

App.propTypes = {
  validating: PropTypes.bool,
  user: PropTypes.object,
  token: PropTypes.string,
  theme: PropTypes.string,
  setTheme: PropTypes.func,
  userRoles: PropTypes.object,
  getUserRoles: PropTypes.func,
};

const mapStateToProps = (state) => {
  const validating = state.getIn(['app', 'authentication', 'validating']);
  const user = state.getIn(['app', 'authentication', 'user']).toJS();
  const token = state.getIn(['app', 'authentication', 'token']) || null;
  const theme = state.getIn(['app', 'theme']);
  const userRoles = state.getIn(['app', ' userRoles']);
  return {
    validating, user, token, theme, userRoles,
  };
};

export function mapDispatchToProps(dispatch) {
  return {
    setTheme: (theme) => dispatch(setTheme(theme)),
    getUserRoles: () => dispatch(getUserRoles()),
    getServerProperties: () => dispatch(getServerProperties()),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withReducer = injectReducer({ key: 'app', reducer });
const withSaga = injectSaga({ key: 'app', saga });

export default compose(
  withReducer,
  withSaga,
  withConnect,
  withTranslation(),
)(App);
